import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import * as styles from './style'
export interface Props {
  title?: string
  initialStart: number
  initialEnd: number
  min: number
  max: number
  step: number
  onChangeTimeFilter: (minValue: number, maxValue: number) => void
}

const MultiRangeSlider = ({
  onChangeTimeFilter,
  initialStart,
  initialEnd,
  min,
  max,
  step,
  title,
}: Props): JSX.Element => {
  const [sliderMinValue, setSliderMinValue] = useState(initialStart)
  const [sliderMaxValue, setSliderMaxValue] = useState(initialEnd)
  const [minValue, setMinValue] = useState<number | undefined>(undefined)
  const [maxValue, setMaxValue] = useState<number | undefined>(undefined)
  const handleOnKeyUp = (): void => {
    setMinValue((prev) => (prev !== sliderMinValue ? sliderMinValue : prev))
    setMaxValue((prev) => (prev !== sliderMaxValue ? sliderMaxValue : prev))
  }

  useEffect(() => {
    //this will watch for any change in initialStart/initialEnd to set the slider accordingly.
    setSliderMinValue(initialStart)
    setSliderMaxValue(initialEnd)
  }, [initialStart, initialEnd])

  useEffect(() => {
    if (minValue !== undefined && maxValue !== undefined) onChangeTimeFilter(minValue, maxValue)
  }, [minValue, maxValue])

  const [pillPosition, setPillPosition] = useState({
    left: {
      pillMarginLeft: 0,
      pillMarginChangeStep: -16,
      arrowMarginLeft: 0,
      arrowMarginChangeStep: 6,
    },
    right: {
      pillMarginLeft: 0,
      pillMarginChangeStep: 16,
      arrowMarginLeft: 0,
      arrowMarginChangeStep: -6,
    },
    diff: sliderMaxValue - sliderMinValue,
  })
  const handlePillPositionChange = (): void => {
    const diff = sliderMaxValue - sliderMinValue
    if (diff < pillPosition.diff && diff < 6)
      setPillPosition({
        left: {
          pillMarginLeft: pillPosition.left.pillMarginLeft + pillPosition.left.pillMarginChangeStep,
          pillMarginChangeStep: -16,
          arrowMarginLeft: pillPosition.left.arrowMarginLeft + pillPosition.left.arrowMarginChangeStep,
          arrowMarginChangeStep: 6,
        },
        right: {
          pillMarginLeft: pillPosition.right.pillMarginLeft + pillPosition.right.pillMarginChangeStep,
          pillMarginChangeStep: 16,
          arrowMarginLeft: pillPosition.right.arrowMarginLeft + pillPosition.right.arrowMarginChangeStep,
          arrowMarginChangeStep: -6,
        },
        diff: diff,
      })
    else if (diff > pillPosition.diff && diff < 6)
      setPillPosition({
        left: {
          pillMarginLeft: pillPosition.left.pillMarginLeft - pillPosition.left.pillMarginChangeStep,
          pillMarginChangeStep: -16,
          arrowMarginLeft: pillPosition.left.arrowMarginLeft - pillPosition.left.arrowMarginChangeStep,
          arrowMarginChangeStep: 6,
        },
        right: {
          pillMarginLeft: pillPosition.right.pillMarginLeft - pillPosition.right.pillMarginChangeStep,
          pillMarginChangeStep: 16,
          arrowMarginLeft: pillPosition.right.arrowMarginLeft - pillPosition.right.arrowMarginChangeStep,
          arrowMarginChangeStep: -6,
        },
        diff: diff,
      })
    else
      setPillPosition({
        left: {
          pillMarginLeft: 0,
          pillMarginChangeStep: -16,
          arrowMarginLeft: 0,
          arrowMarginChangeStep: 6,
        },
        right: {
          pillMarginLeft: 0,
          pillMarginChangeStep: 16,
          arrowMarginLeft: 0,
          arrowMarginChangeStep: -6,
        },
        diff: diff,
      })
  }

  useEffect(() => {
    handlePillPositionChange()
  }, [sliderMinValue, sliderMaxValue])

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    const value = parseFloat(e.target.value)
    const newMinVal = Math.min(value, sliderMaxValue - step)
    setSliderMinValue(newMinVal)
  }
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    const value = parseFloat(e.target.value)
    const newMaxVal = Math.max(value, sliderMinValue + step)
    setSliderMaxValue(newMaxVal)
  }
  const minPos = ((sliderMinValue - min) / (max - min)) * 100
  const maxPos = ((sliderMaxValue - min) / (max - min)) * 100
  const startHour = dayjs().set('hour', sliderMinValue).format('ha')
  var endHour = dayjs().set('hour', sliderMaxValue).format('ha')
  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      <div className={styles.rangeContainer}>
        <div className={styles.defaultRangeContainer}>
          <input
            type="range"
            value={sliderMinValue}
            onChange={handleMinChange}
            onMouseUp={handleOnKeyUp}
            onTouchEnd={handleOnKeyUp}
            min={min}
            max={max}
            step={step}
            className={styles.defaultRange}
            style={{ zIndex: sliderMaxValue - sliderMinValue === 1 && sliderMinValue !== 0 ? 4 : 3 }}
          />
          <input
            type="range"
            value={sliderMaxValue}
            onChange={handleMaxChange}
            onMouseUp={handleOnKeyUp}
            onTouchEnd={handleOnKeyUp}
            min={min}
            max={max}
            step={step}
            className={styles.defaultRange}
          />

          <div className={styles.customRangeContainer}>
            <div className={styles.selectedRange} style={{ left: `${minPos}%`, right: `${100 - maxPos}%` }}></div>
            <div
              className={styles.handle}
              style={{
                left: `calc(${minPos}% - ${sliderMinValue}px)`,
                zIndex: sliderMaxValue - sliderMinValue === 1 && sliderMinValue !== 0 ? 3 : 0,
              }}
            >
              <div
                className={`${styles.pill} ${styles.pillLeft} drop-shadow`}
                style={{
                  marginLeft: `${pillPosition.left.pillMarginLeft}%`,
                  zIndex: sliderMaxValue - sliderMinValue === 1 && sliderMinValue !== 0 ? 3 : 1,
                }}
              >
                <div
                  className={`${styles.pillArrow}`}
                  style={{ marginLeft: `${pillPosition.left.arrowMarginLeft}%` }}
                ></div>
                {startHour}
              </div>
            </div>
            <div className={styles.handle} style={{ right: `calc(${100 - maxPos}% - ${max - sliderMaxValue}px)` }}>
              <div
                className={`${styles.pill} ${styles.pillRight} drop-shadow`}
                style={{ marginLeft: `${pillPosition.right.pillMarginLeft}%` }}
              >
                <div
                  className={styles.pillArrow}
                  style={{ marginLeft: `${pillPosition.right.arrowMarginLeft}%` }}
                ></div>
                {endHour}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default MultiRangeSlider
